import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { motion } from "framer-motion";
import {
  BookOpen, Banknote, BarChart3, ArrowLeftRight, RefreshCw, Users,
  CheckCircle2, Calendar, ArrowRight, Phone, Mail, Facebook, Instagram,
  Menu, X, ShieldCheck, Clock, Sparkles, Target, ChevronDown,
} from "lucide-react";
import { t, CALENDLY_URL, CONTACT } from "@/content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO = "https://customer-assets.emergentagent.com/job_ledger-sync-63/artifacts/30a6kthy_1_PA_LOGO%20SOFT%20NAVY.png";
const HERO_IMG = "https://customer-assets.emergentagent.com/job_ledger-sync-63/artifacts/tqwe1fvo_ChatGPT%20Image%2018%20jun%202026%2C%2001_43_16%20p.m..png";
const WHY_IMG = "https://customer-assets.emergentagent.com/job_ledger-sync-63/artifacts/y6yaap3t_ChatGPT%20Image%2018%20jun%202026%2C%2001_43_28%20p.m..png";

const iconMap = { BookOpen, Banknote, BarChart3, ArrowLeftRight, RefreshCw, Users };

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

function Navbar({ lang, setLang, c }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "services", label: c.nav.services },
    { id: "why", label: c.nav.why },
    { id: "process", label: c.nav.process },
    { id: "faq", label: c.nav.faq },
    { id: "contact", label: c.nav.contact },
  ];

  const go = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-white/95 backdrop-blur shadow-[0_8px_30px_-12px_rgba(6,79,131,0.25)]" : "bg-transparent"}`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between h-20">
        <button onClick={() => go("top")} data-testid="logo-home" className="flex items-center gap-3">
          <img src={LOGO} alt="Prime Ally LLC" className="h-12 w-auto" />
        </button>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => go(l.id)}
              className="link-underline text-[15px] font-medium text-[#0d1b26] hover:text-[#064F83] transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center rounded-full border border-[#064F83]/25 overflow-hidden text-sm font-semibold">
            <button
              data-testid="lang-es"
              onClick={() => setLang("es")}
              className={`px-3 py-1.5 transition-colors ${lang === "es" ? "bg-[#064F83] text-white" : "text-[#064F83]"}`}
            >ES</button>
            <button
              data-testid="lang-en"
              onClick={() => setLang("en")}
              className={`px-3 py-1.5 transition-colors ${lang === "en" ? "bg-[#064F83] text-white" : "text-[#064F83]"}`}
            >EN</button>
          </div>
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-book-btn"
            className="btn-press hidden md:inline-flex items-center gap-2 bg-[#064F83] text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-[#043555] shadow-lg shadow-[#064F83]/20"
          >
            <Calendar size={16} /> {c.nav.book}
          </a>
          <button data-testid="mobile-menu-btn" onClick={() => setOpen(!open)} className="lg:hidden text-[#064F83] p-2">
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-gray-100 px-5 py-4 space-y-1">
          {links.map((l) => (
            <button key={l.id} onClick={() => go(l.id)} className="block w-full text-left py-3 font-medium text-[#0d1b26]">
              {l.label}
            </button>
          ))}
          <div className="flex items-center gap-2 pt-2">
            <button onClick={() => setLang("es")} className={`px-4 py-1.5 rounded-full text-sm font-semibold ${lang === "es" ? "bg-[#064F83] text-white" : "border border-[#064F83]/30 text-[#064F83]"}`}>ES</button>
            <button onClick={() => setLang("en")} className={`px-4 py-1.5 rounded-full text-sm font-semibold ${lang === "en" ? "bg-[#064F83] text-white" : "border border-[#064F83]/30 text-[#064F83]"}`}>EN</button>
          </div>
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-2 bg-[#064F83] text-white px-5 py-3 rounded-full text-sm font-semibold w-full justify-center">
            <Calendar size={16} /> {c.nav.book}
          </a>
        </div>
      )}
    </header>
  );
}

function Hero({ c }) {
  return (
    <section id="top" className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
      <div className="absolute inset-0 dot-grid opacity-70" />
      <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-[#064F83]/5 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <motion.span
            initial="hidden" animate="show" variants={fadeUp}
            className="inline-flex items-center gap-2 bg-[#064F83]/8 text-[#064F83] px-4 py-2 rounded-full text-sm font-semibold mb-6 border border-[#064F83]/15"
          >
            <Sparkles size={15} /> {c.hero.badge}
          </motion.span>
          <motion.h1
            initial="hidden" animate="show" custom={1} variants={fadeUp}
            className="text-balance text-4xl md:text-[3.4rem] leading-[1.07] font-extrabold tracking-tight text-[#0d1b26]"
          >
            {c.hero.title1}{" "}
            <span className="text-[#064F83] relative">
              {c.hero.title2}
              <span className="absolute left-0 -bottom-1 w-full h-2 bg-[#C9982E]/30 -z-0" />
            </span>
          </motion.h1>
          <motion.p
            initial="hidden" animate="show" custom={2} variants={fadeUp}
            className="mt-6 text-lg text-gray-600 max-w-xl leading-relaxed"
          >
            {c.hero.sub}
          </motion.p>
          <motion.div
            initial="hidden" animate="show" custom={3} variants={fadeUp}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <a
              href={CALENDLY_URL} target="_blank" rel="noopener noreferrer"
              data-testid="hero-book-btn"
              className="btn-press inline-flex items-center justify-center gap-2 bg-[#064F83] text-white px-7 py-4 rounded-full font-semibold hover:bg-[#043555] shadow-xl shadow-[#064F83]/25"
            >
              <Calendar size={18} /> {c.hero.ctaBook}
            </a>
            <button
              onClick={() => document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="hero-services-btn"
              className="btn-press inline-flex items-center justify-center gap-2 bg-white text-[#064F83] px-7 py-4 rounded-full font-semibold border-2 border-[#064F83]/15 hover:border-[#064F83]/40"
            >
              {c.hero.ctaServices} <ArrowRight size={18} />
            </button>
          </motion.div>
          <motion.div
            initial="hidden" animate="show" custom={4} variants={fadeUp}
            className="mt-10 flex flex-wrap gap-x-7 gap-y-3"
          >
            {[c.hero.stat1, c.hero.stat2, c.hero.stat3].map((s) => (
              <div key={s} className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <CheckCircle2 size={18} className="text-[#C9982E]" /> {s}
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div className="relative rounded-[28px] overflow-hidden shadow-2xl shadow-[#064F83]/20 border-4 border-white">
            <img src={HERO_IMG} alt="Prime Ally bookkeeping team" className="w-full h-auto object-cover object-top" />
          </div>
          <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-5 flex items-center gap-4 border border-gray-100">
            <div className="w-12 h-12 rounded-xl navy-grad flex items-center justify-center text-white">
              <BarChart3 size={24} />
            </div>
            <div>
              <p className="text-2xl font-extrabold text-[#064F83] leading-none">P&L</p>
              <p className="text-xs text-gray-500 mt-1">{c.hero.stat1}</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function TrustStrip({ c }) {
  return (
    <section className="bg-[#064F83]">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-7 flex flex-col lg:flex-row items-center gap-5 lg:gap-10">
        <p className="text-white/90 text-sm md:text-[15px] font-medium lg:max-w-sm text-center lg:text-left">{c.trust.line}</p>
        <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4 w-full">
          {c.trust.items.map((it) => (
            <div key={it} className="flex items-center gap-2 text-white/95 text-sm font-semibold justify-center lg:justify-start">
              <CheckCircle2 size={16} className="text-[#E5C66A] shrink-0" /> {it}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SectionHead({ tag, title, sub, center }) {
  return (
    <motion.div
      initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.4 }} variants={fadeUp}
      className={`${center ? "text-center mx-auto" : ""} max-w-2xl mb-14`}
    >
      <span className="text-[#C9982E] font-bold text-sm uppercase tracking-[0.18em]">{tag}</span>
      <h2 className="mt-3 text-3xl md:text-[2.6rem] font-extrabold tracking-tight text-[#0d1b26] leading-tight">{title}</h2>
      <div className={`section-line mt-5 ${center ? "mx-auto" : ""}`} />
      {sub && <p className="mt-5 text-lg text-gray-600 leading-relaxed">{sub}</p>}
    </motion.div>
  );
}

function Services({ c }) {
  return (
    <section id="services" className="py-24 bg-[#f4f7fa]">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <SectionHead tag={c.services.tag} title={c.services.title} sub={c.services.sub} center />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
          {c.services.list.map((s, i) => {
            const Icon = iconMap[s.icon] || BookOpen;
            return (
              <motion.div
                key={s.title}
                initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} custom={i} variants={fadeUp}
                data-testid={`service-card-${i}`}
                className="card-hover bg-white rounded-2xl p-8 border border-gray-100"
              >
                <div className="w-14 h-14 rounded-2xl navy-grad flex items-center justify-center text-white mb-6 shadow-lg shadow-[#064F83]/20">
                  <Icon size={26} />
                </div>
                <h3 className="text-xl font-bold text-[#0d1b26] mb-3">{s.title}</h3>
                <p className="text-gray-600 leading-relaxed text-[15px]">{s.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Why({ c }) {
  const icons = [Target, ShieldCheck, Clock, Sparkles];
  return (
    <section id="why" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.7 }}
          className="relative order-2 lg:order-1"
        >
          <div className="rounded-[28px] overflow-hidden shadow-2xl shadow-[#064F83]/15 border-4 border-white">
            <img src={WHY_IMG} alt="Bookkeeping at Prime Ally" className="w-full h-[480px] object-cover" />
          </div>
          <div className="absolute -top-5 -right-5 navy-grad text-white rounded-2xl px-6 py-5 shadow-xl max-w-[180px]">
            <p className="text-sm font-semibold leading-snug">QuickBooks Online</p>
            <p className="text-xs text-white/70 mt-1">{c.hero.badge}</p>
          </div>
        </motion.div>

        <div className="order-1 lg:order-2">
          <SectionHead tag={c.why.tag} title={c.why.title} sub={c.why.sub} />
          <div className="space-y-6">
            {c.why.points.map((p, i) => {
              const Icon = icons[i];
              return (
                <motion.div
                  key={p.title}
                  initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} custom={i} variants={fadeUp}
                  className="flex gap-4"
                >
                  <div className="w-11 h-11 rounded-xl bg-[#064F83]/8 flex items-center justify-center text-[#064F83] shrink-0">
                    <Icon size={20} />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#0d1b26] mb-1">{p.title}</h4>
                    <p className="text-gray-600 text-[15px] leading-relaxed">{p.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function Process({ c }) {
  return (
    <section id="process" className="py-24 bg-[#f4f7fa]">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <SectionHead tag={c.process.tag} title={c.process.title} center />
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-7">
          {c.process.steps.map((s, i) => (
            <motion.div
              key={s.n}
              initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} custom={i} variants={fadeUp}
              className="relative bg-white rounded-2xl p-8 border border-gray-100 card-hover"
            >
              <span className="text-5xl font-extrabold text-[#064F83]/12">{s.n}</span>
              <h3 className="text-lg font-bold text-[#0d1b26] mt-3 mb-2">{s.title}</h3>
              <p className="text-gray-600 text-[15px] leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Faq({ c }) {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="py-24 bg-white">
      <div className="max-w-3xl mx-auto px-5 md:px-8">
        <SectionHead tag={c.faq.tag} title={c.faq.title} sub={c.faq.sub} center />
        <div className="space-y-4">
          {c.faq.items.map((it, i) => {
            const isOpen = open === i;
            return (
              <motion.div
                key={i}
                initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} custom={i} variants={fadeUp}
                data-testid={`faq-item-${i}`}
                className={`rounded-2xl border transition-colors ${isOpen ? "border-[#064F83]/30 bg-[#f4f7fa]" : "border-gray-150 bg-white"} `}
                style={{ borderColor: isOpen ? "rgba(6,79,131,0.3)" : "#e8edf2" }}
              >
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  data-testid={`faq-question-${i}`}
                  className="w-full flex items-center justify-between gap-4 text-left px-6 py-5"
                >
                  <span className="font-bold text-[#0d1b26] text-[16px]">{it.q}</span>
                  <ChevronDown
                    size={22}
                    className={`shrink-0 text-[#064F83] transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                  />
                </button>
                <div
                  className="grid transition-all duration-300 ease-out"
                  style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}
                >
                  <div className="overflow-hidden">
                    <p className="px-6 pb-5 text-gray-600 leading-relaxed text-[15px]">{it.a}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CtaBand({ c }) {
  return (
    <section className="py-20">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <div className="navy-grad rounded-[32px] px-8 md:px-16 py-14 text-center relative overflow-hidden">
          <div className="absolute inset-0 dot-grid opacity-10" />
          <div className="relative">
            <motion.h2
              initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
              className="text-3xl md:text-[2.6rem] font-extrabold text-white leading-tight max-w-3xl mx-auto"
            >
              {c.cta.title}
            </motion.h2>
            <p className="mt-5 text-white/80 text-lg max-w-2xl mx-auto">{c.cta.sub}</p>
            <a
              href={CALENDLY_URL} target="_blank" rel="noopener noreferrer"
              data-testid="cta-book-btn"
              className="btn-press mt-9 inline-flex items-center gap-2 bg-[#C9982E] text-[#032539] px-8 py-4 rounded-full font-bold hover:bg-[#E5C66A] shadow-xl"
            >
              <Calendar size={18} /> {c.cta.button}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function Contact({ c }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", service: "", message: "" });
  const [status, setStatus] = useState("idle");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm({ name: "", email: "", phone: "", company: "", service: "", message: "" });
    } catch (err) {
      setStatus("error");
    }
  };

  const input = "w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#064F83] focus:ring-2 focus:ring-[#064F83]/15 outline-none transition text-[15px]";

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-5 gap-12">
        <div className="lg:col-span-2">
          <SectionHead tag={c.contact.tag} title={c.contact.title} sub={c.contact.sub} />
          <div className="space-y-5">
            <p className="font-bold text-[#0d1b26]">{c.contact.infoTitle}</p>
            <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="flex items-center gap-4 group">
              <span className="w-11 h-11 rounded-xl bg-[#064F83]/8 flex items-center justify-center text-[#064F83]"><Mail size={19} /></span>
              <span className="text-gray-700 group-hover:text-[#064F83] transition-colors">{CONTACT.email}</span>
            </a>
            <a href={`tel:${CONTACT.phoneHref}`} data-testid="contact-phone-link" className="flex items-center gap-4 group">
              <span className="w-11 h-11 rounded-xl bg-[#064F83]/8 flex items-center justify-center text-[#064F83]"><Phone size={19} /></span>
              <span className="text-gray-700 group-hover:text-[#064F83] transition-colors">{CONTACT.phone}</span>
            </a>
            <div className="pt-3">
              <p className="font-bold text-[#0d1b26] mb-3">{c.contact.followUs}</p>
              <div className="flex gap-3">
                <a href={CONTACT.facebook} target="_blank" rel="noopener noreferrer" data-testid="social-facebook" className="btn-press w-11 h-11 rounded-xl navy-grad flex items-center justify-center text-white hover:opacity-90">
                  <Facebook size={19} />
                </a>
                <a href={CONTACT.instagram} target="_blank" rel="noopener noreferrer" data-testid="social-instagram" className="btn-press w-11 h-11 rounded-xl navy-grad flex items-center justify-center text-white hover:opacity-90">
                  <Instagram size={19} />
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-3">
          <form onSubmit={submit} data-testid="contact-form" className="bg-[#f4f7fa] rounded-3xl p-7 md:p-10 border border-gray-100">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-semibold text-[#0d1b26] mb-2">{c.contact.name} *</label>
                <input data-testid="input-name" required value={form.name} onChange={(e) => update("name", e.target.value)} className={input} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#0d1b26] mb-2">{c.contact.email} *</label>
                <input data-testid="input-email" type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} className={input} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#0d1b26] mb-2">{c.contact.phone}</label>
                <input data-testid="input-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className={input} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#0d1b26] mb-2">{c.contact.company}</label>
                <input data-testid="input-company" value={form.company} onChange={(e) => update("company", e.target.value)} className={input} />
              </div>
            </div>
            <div className="mt-5">
              <label className="block text-sm font-semibold text-[#0d1b26] mb-2">{c.contact.service}</label>
              <select data-testid="input-service" value={form.service} onChange={(e) => update("service", e.target.value)} className={input}>
                <option value="">{c.contact.servicePlaceholder}</option>
                {c.services.list.map((s) => <option key={s.title} value={s.title}>{s.title}</option>)}
              </select>
            </div>
            <div className="mt-5">
              <label className="block text-sm font-semibold text-[#0d1b26] mb-2">{c.contact.message} *</label>
              <textarea data-testid="input-message" required rows={4} value={form.message} onChange={(e) => update("message", e.target.value)} className={input} />
            </div>

            {status === "success" && (
              <p data-testid="form-success" className="mt-5 text-green-700 bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm font-medium">{c.contact.success}</p>
            )}
            {status === "error" && (
              <p data-testid="form-error" className="mt-5 text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm font-medium">{c.contact.error}</p>
            )}

            <button
              type="submit" data-testid="form-submit-btn" disabled={status === "sending"}
              className="btn-press mt-6 inline-flex items-center gap-2 bg-[#064F83] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#043555] shadow-lg shadow-[#064F83]/20 disabled:opacity-60"
            >
              {status === "sending" ? c.contact.sending : c.contact.send} <ArrowRight size={18} />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

function Footer({ c, lang, setLang }) {
  return (
    <footer className="navy-grad text-white">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 grid md:grid-cols-3 gap-10">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-white rounded-xl p-2">
              <img src={LOGO} alt="Prime Ally LLC" className="h-12 w-auto" />
            </div>
          </div>
          <p className="text-white/70 text-sm leading-relaxed max-w-xs">{c.footer.desc}</p>
          <div className="flex gap-3 mt-5">
            <a href={CONTACT.facebook} target="_blank" rel="noopener noreferrer" className="btn-press w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center hover:bg-white/20"><Facebook size={17} /></a>
            <a href={CONTACT.instagram} target="_blank" rel="noopener noreferrer" className="btn-press w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center hover:bg-white/20"><Instagram size={17} /></a>
          </div>
        </div>

        <div>
          <p className="font-bold mb-4">{c.footer.quick}</p>
          <ul className="space-y-2.5 text-white/70 text-sm">
            {["services", "why", "process", "faq", "contact"].map((id) => (
              <li key={id}>
                <button onClick={() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" })} className="hover:text-[#E5C66A] transition-colors">
                  {c.nav[id]}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="font-bold mb-4">{c.contact.infoTitle}</p>
          <ul className="space-y-3 text-white/70 text-sm">
            <li className="flex items-center gap-3"><Mail size={16} className="text-[#E5C66A]" /> <a href={`mailto:${CONTACT.email}`} className="hover:text-white">{CONTACT.email}</a></li>
            <li className="flex items-center gap-3"><Phone size={16} className="text-[#E5C66A]" /> <a href={`tel:${CONTACT.phoneHref}`} className="hover:text-white">{CONTACT.phone}</a></li>
          </ul>
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-press mt-5 inline-flex items-center gap-2 bg-[#C9982E] text-[#032539] px-5 py-2.5 rounded-full text-sm font-bold hover:bg-[#E5C66A]">
            <Calendar size={15} /> {c.nav.book}
          </a>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-white/60">
          <p>© {new Date().getFullYear()} Prime Ally LLC. {c.footer.rights}</p>
          <p className="uppercase tracking-[0.15em] text-xs">{c.footer.tagline}</p>
        </div>
      </div>
    </footer>
  );
}

function App() {
  const [lang, setLang] = useState("es");
  const c = t[lang];
  return (
    <div className="App">
      <Navbar lang={lang} setLang={setLang} c={c} />
      <Hero c={c} />
      <TrustStrip c={c} />
      <Services c={c} />
      <Why c={c} />
      <Process c={c} />
      <Faq c={c} />
      <CtaBand c={c} />
      <Contact c={c} />
      <Footer c={c} lang={lang} setLang={setLang} />
    </div>
  );
}

export default App;
